import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Users, Share2, Lightbulb, Save, Lock, Landmark, PlayCircle, ShieldCheck, FileUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = 'safe_partnership_profile_draft';

const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
};

const Label = ({ children, required }) => (
  <label className="block text-sm font-semibold text-slate-900 mb-2">
    {children}{required && <span className="text-rose-600"> *</span>}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900 transition-colors"
  />
);

const Area = ({ rows = 4, ...props }) => (
  <textarea
    rows={rows}
    {...props}
    className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900 transition-colors"
  />
);

const CheckGrid = ({ options, selected, onToggle, testid }) => (
  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid={testid}>
    {options.map((opt) => (
      <label key={opt} className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer group">
        <input
          type="checkbox"
          checked={selected.includes(opt)}
          onChange={() => onToggle(opt)}
          className="mt-1 w-4 h-4 accent-slate-900 shrink-0"
        />
        <span className="group-hover:text-slate-900 transition-colors">{opt}</span>
      </label>
    ))}
  </div>
);

const SectionHeading = ({ eyebrow, title, children }) => (
  <div className="mb-8">
    {eyebrow && <div className="text-xs font-bold tracking-[0.18em] text-slate-500 uppercase mb-3">{eyebrow}</div>}
    <h2 className="text-lg md:text-lg font-bold text-slate-900">{title}</h2>
    {children && <p className="text-sm text-slate-600 mt-3 max-w-3xl leading-relaxed">{children}</p>}
  </div>
);

const Done = ({ title, body, onAgain, testid, againLabel }) => (
  <div className="bg-white border border-slate-200 p-10 text-center" data-testid={testid}>
    <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-4" strokeWidth={1.5} />
    <h3 className="text-lg font-bold text-slate-900 mb-3">{title}</h3>
    <p className="text-sm text-slate-600 max-w-xl mx-auto leading-relaxed">{body}</p>
    {onAgain && (
      <button onClick={onAgain} className="btn-primary mt-6" data-testid={`${testid}-again`}>
        {againLabel}
      </button>
    )}
  </div>
);

const emptyProfile = {
  email: '', full_name: '', about_you: '', organisations: '', job_title: '', based_in: '',
  passionate_about: '', next_12_24_months: '', expertise_offer: '',
  contribution_areas: [], contribution_other: '', sectors: [], sectors_other: '',
  people_safe_should_know: '', intro_willingness: '', opportunities_to_explore: '',
  joint_initiatives: '', how_safe_can_help: '', council_member_to_meet: '',
  who_should_we_know: '', greatest_contribution: '', anything_else: '',
  linkedin: '', phone: '', website: '', consent: false, company_fax: '',
};

const emptyReferral = {
  referrer_email: '', referrer_name: '', person_name: '', person_title: '', person_email: '',
  organisation: '', organisation_website: '', linkedin: '', country: '', domain: '',
  sectors: [], how_you_know_them: '', why_valuable: '', introduction_available: '', company_fax: '',
};

const emptyOpportunity = {
  submitter_email: '', submitter_name: '', title: '', opportunity_type: '', organisation: '',
  country: '', domain: '', description: '', deadline: '', why_it_matters: '',
  support_needed: '', company_fax: '',
};

const emptyFunding = {
  submitter_email: '', submitter_name: '', funding_world: '', organisation: '', funder_type: '',
  contact_name: '', contact_role: '', relationship_nature: '', access_depth: '',
  contacts_at_org: '', typical_ticket: '', regions: [], willingness: '', notes: '',
  company_fax: '',
};

const emptyVetting = {
  submitter_email: '', submitter_name: '', check_country: '', check_issued_on: '',
  id_document_type: '', notes: '', consent: false, company_fax: '',
};

const ID_DOCUMENT_TYPES = ['Passport', 'National identity card', "Driver's licence", 'Other government photo ID'];

const PartnershipHubPage = () => {
  const [ref, setRef] = useState({
    domains: [], sectors: [], opportunity_types: [], intro_willingness: [],
    funding_worlds: [], funder_types: {}, access_depth: [], ticket_bands: [],
    contacts_at_org: [], funding_regions: [], funding_willingness: [],
  });
  const [profile, setProfile] = useState(emptyProfile);
  const [referral, setReferral] = useState(emptyReferral);
  const [opportunity, setOpportunity] = useState(emptyOpportunity);
  const [funding, setFunding] = useState(emptyFunding);
  const [vetting, setVetting] = useState(emptyVetting);
  const [policeCheckFile, setPoliceCheckFile] = useState(null);
  const [idPhotoFile, setIdPhotoFile] = useState(null);
  const [busy, setBusy] = useState('');
  const [doneProfile, setDoneProfile] = useState(false);
  const [doneReferral, setDoneReferral] = useState(false);
  const [doneOpportunity, setDoneOpportunity] = useState(false);
  const [doneFunding, setDoneFunding] = useState(false);
  const [doneVetting, setDoneVetting] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState('');
  const [draftLoaded, setDraftLoaded] = useState(false);

  useEffect(() => {
    document.title = 'SAFE Founding Council — Partnership Hub';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    axios.get(`${API}/network/reference-data`).then((r) => setRef(r.data)).catch(() => {});
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        setProfile({ ...emptyProfile, ...JSON.parse(saved) });
        setDraftSavedAt('restored');
      }
    } catch (e) { /* ignore malformed draft */ }
    setDraftLoaded(true);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!draftLoaded) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(profile)); } catch (e) { /* quota */ }
  }, [profile, draftLoaded]);

  const pField = (k) => ({
    value: profile[k],
    onChange: (e) => setProfile((p) => ({ ...p, [k]: e.target.value })),
    'data-testid': `profile-${k.replace(/_/g, '-')}`,
  });
  const rField = (k) => ({
    value: referral[k],
    onChange: (e) => setReferral((p) => ({ ...p, [k]: e.target.value })),
    'data-testid': `referral-${k.replace(/_/g, '-')}`,
  });
  const oField = (k) => ({
    value: opportunity[k],
    onChange: (e) => setOpportunity((p) => ({ ...p, [k]: e.target.value })),
    'data-testid': `opportunity-${k.replace(/_/g, '-')}`,
  });
  const fField = (k) => ({
    value: funding[k],
    onChange: (e) => setFunding((p) => ({ ...p, [k]: e.target.value })),
    'data-testid': `funding-${k.replace(/_/g, '-')}`,
  });

  const vField = (k) => ({
    value: vetting[k],
    onChange: (e) => setVetting((p) => ({ ...p, [k]: e.target.value })),
    'data-testid': `vetting-${k.replace(/_/g, '-')}`,
  });

  const toggle = (setter, key) => (opt) =>
    setter((prev) => ({
      ...prev,
      [key]: prev[key].includes(opt) ? prev[key].filter((x) => x !== opt) : [...prev[key], opt],
    }));

  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    if (!profile.consent) {
      toast.error('Please confirm you are happy for SAFE to hold this information.');
      return;
    }
    setBusy('profile');
    try {
      await axios.post(`${API}/network/profile`, profile);
      localStorage.removeItem(DRAFT_KEY);
      setDoneProfile(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(errMsg(err, 'We could not save your profile. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  const submitReferral = async (e) => {
    e.preventDefault();
    setBusy('referral');
    try {
      await axios.post(`${API}/network/referral`, referral);
      setDoneReferral(true);
      jump('referral');
    } catch (err) {
      toast.error(errMsg(err, 'We could not save that referral. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  const submitOpportunity = async (e) => {
    e.preventDefault();
    setBusy('opportunity');
    try {
      await axios.post(`${API}/network/opportunity`, opportunity);
      setDoneOpportunity(true);
      jump('opportunity');
    } catch (err) {
      toast.error(errMsg(err, 'We could not save that opportunity. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  const submitFunding = async (e) => {
    e.preventDefault();
    if (!funding.funding_world) {
      toast.error('Please choose whether this is commercial investment or philanthropic funding.');
      return;
    }
    setBusy('funding');
    try {
      await axios.post(`${API}/network/funding-connection`, funding);
      setDoneFunding(true);
      jump('funding');
    } catch (err) {
      toast.error(errMsg(err, 'We could not save that funding connection. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  const submitVetting = async (e) => {
    e.preventDefault();
    if (!policeCheckFile || !idPhotoFile) {
      toast.error('Please attach both your criminal history check and your photo-ID photo.');
      return;
    }
    if (!vetting.consent) {
      toast.error('Please confirm you consent to SAFE verifying these documents.');
      return;
    }
    setBusy('vetting');
    try {
      const fd = new FormData();
      Object.entries(vetting).forEach(([k, v]) => fd.append(k, k === 'consent' ? String(v) : v));
      fd.append('police_check', policeCheckFile);
      fd.append('id_photo', idPhotoFile);
      await axios.post(`${API}/network/vetting`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setDoneVetting(true);
      jump('vetting');
    } catch (err) {
      toast.error(errMsg(err, 'We could not upload your documents. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  const anotherReferral = () => {
    setReferral({ ...emptyReferral, referrer_email: referral.referrer_email, referrer_name: referral.referrer_name });
    setDoneReferral(false);
  };
  const anotherOpportunity = () => {
    setOpportunity({ ...emptyOpportunity, submitter_email: opportunity.submitter_email, submitter_name: opportunity.submitter_name });
    setDoneOpportunity(false);
  };
  const anotherFunding = () => {
    setFunding({ ...emptyFunding, submitter_email: funding.submitter_email, submitter_name: funding.submitter_name });
    setDoneFunding(false);
  };

  const navCards = [
    { id: 'profile', icon: Users, step: 'Step 1', title: 'Partnership Profile',
      note: 'Fill in once — 10 to 12 minutes', repeat: false },
    { id: 'referral', icon: Share2, step: 'Step 2', title: 'Add a Referral',
      note: 'One person per form', repeat: true },
    { id: 'opportunity', icon: Lightbulb, step: 'Step 3', title: 'Add an Opportunity',
      note: 'One opportunity per form', repeat: true },
    { id: 'funding', icon: Landmark, step: 'Step 4', title: 'Add a Funding Connection',
      note: 'One organisation per form', repeat: true },
    { id: 'vetting', icon: ShieldCheck, step: 'Step 5', title: 'Police Check & ID',
      note: 'Required once — held securely', repeat: false },
  ];

  return (
    <div data-testid="partnership-hub-page" className="bg-slate-50">
      {/* Hero */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-24">
          <div className="flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-slate-400 mb-6">
            <Lock className="w-3.5 h-3.5" strokeWidth={2} />
            Private page — SAFE Founding Council
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Partnership Hub
          </h1>
          <p className="text-base md:text-lg text-slate-300 mt-6 max-w-3xl leading-relaxed">
            Building relationships. Creating opportunities. Shaping SAFE together.
          </p>
          <div className="mt-8 space-y-4 max-w-3xl text-sm text-slate-400 leading-relaxed">
            <p>
              Thank you for being part of the SAFE Founding Council. SAFE is built on trusted
              relationships, shared expertise and a collective commitment to creating meaningful
              change. This is the one place to tell us about your work, introduce us to people you
              think we should know, and flag opportunities worth pursuing.
            </p>
            <p>
              Everything you submit here is held privately by SAFE and is visible only to authorised
              SAFE administrators. Nothing is published, and nothing is shared with other Council
              members without your agreement. Please answer openly, sharing only what you feel
              comfortable sharing.
            </p>
          </div>
        </div>
      </section>

      {/* Anchor buttons */}
      <section className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <p className="text-xs font-bold tracking-[0.14em] uppercase text-slate-500 mb-4">
            Five steps — watch the walk-through below first
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {navCards.map((c) => (
              <button
                key={c.id}
                onClick={() => jump(c.id)}
                data-testid={`hub-jump-${c.id}`}
                className="group text-left border border-slate-300 hover:border-slate-900 bg-white px-5 py-4 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <c.icon className="w-5 h-5 text-slate-900 shrink-0 mt-1" strokeWidth={1.75} />
                  <div>
                    <div className="text-sm font-bold tracking-[0.14em] uppercase text-slate-900">
                      {c.step}
                    </div>
                    <div className="text-sm font-bold text-slate-900 mt-1.5 min-h-[2.5rem]">{c.title}</div>
                    <div className={c.repeat
                      ? 'text-xs text-slate-500 mt-1'
                      : 'text-xs font-bold text-slate-800 mt-1'}>
                      {c.note}
                    </div>
                    {c.repeat ? (
                      <div className="text-xs font-semibold text-emerald-700 mt-1.5">
                        Submit as many times as you like
                      </div>
                    ) : (
                      <div className="text-xs font-semibold text-slate-500 mt-1.5">
                        Only needed once
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Walk-through video */}
      <section className="border-b border-slate-200 bg-white" data-testid="hub-walkthrough">
        <div className="max-w-3xl mx-auto px-6 py-12 md:py-14">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-testid="hub-start-here">
            Start here
          </h2>
          <p className="text-sm md:text-base text-slate-600 mt-3 leading-relaxed">
            A two-minute walk-through of the steps below — what each form is for, and which
            ones you can come back and submit again and again.
          </p>
          <div className="flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mt-8 mb-5">
            <PlayCircle className="w-4 h-4 text-slate-900" strokeWidth={1.75} />
            Video walk-through
          </div>
          <div className="relative w-full overflow-hidden bg-slate-900" style={{ aspectRatio: '7 / 4', borderRadius: '10px' }}>
            <iframe
              src="https://embed.app.guidde.com/playbooks/ffkTrkiyrrgRuabTsg4xg3?mode=videoOnly"
              title="Complete Partnership Profile and Network Building"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              referrerPolicy="unsafe-url"
              allowFullScreen
              allow="clipboard-write"
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
              style={{ border: 0, borderRadius: '10px' }}
              data-testid="hub-walkthrough-video"
            />
          </div>
        </div>
      </section>

      {/* ---------------- Partnership Profile ---------------- */}
      <section id="profile" className="scroll-mt-24 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="Step 1 of 5 — fill in once" title="Partnership Profile">
            An opportunity for us to better understand one another — the work we are each leading,
            where we can support each other, and how we can build SAFE together. Your answers help
            us strengthen the Council, identify collaborations and make meaningful introductions.
            Estimated completion time: 10–12 minutes. Your answers save automatically in this
            browser, so you can close the tab and come back.
          </SectionHeading>

          {doneProfile ? (
            <Done
              testid="profile-success"
              title="Your Partnership Profile is in."
              body="Thank you. This is the beginning of many conversations, collaborations and opportunities. A member of the SAFE team will follow up personally. You can still add referrals and opportunities below at any time."
            />
          ) : (
            <form onSubmit={submitProfile} className="space-y-10" data-testid="profile-form">
              <input type="text" name="company_fax" tabIndex={-1} autoComplete="off"
                value={profile.company_fax}
                onChange={(e) => setProfile((p) => ({ ...p, company_fax: e.target.value }))}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true" />

              <div className="bg-white border border-slate-200 p-8">
                <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-slate-500 mb-6">
                  Section 1 — About you
                </h3>
                <div className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label required>Your full name</Label>
                      <Input {...pField('full_name')} required placeholder="Jane Doe" />
                    </div>
                    <div>
                      <Label required>Your registered email address</Label>
                      <Input {...pField('email')} type="email" required placeholder="jane@organisation.org" />
                      <p className="text-xs text-slate-500 mt-2">
                        Please use the email address SAFE already has on file — this is how we
                        attribute your answers to your Council record.
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label>Tell us a little about yourself</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Beyond your professional biography, what would you like your fellow Council
                      members to know about you?
                    </p>
                    <Area {...pField('about_you')} rows={5} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label>What organisation(s) are you currently involved with?</Label>
                      <Input {...pField('organisations')} />
                    </div>
                    <div>
                      <Label>Your role</Label>
                      <Input {...pField('job_title')} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label>Where are you based?</Label>
                      <Input {...pField('based_in')} placeholder="City, Country" />
                    </div>
                    <div>
                      <Label>LinkedIn profile</Label>
                      <Input {...pField('linkedin')} placeholder="https://linkedin.com/in/…" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label>Website</Label>
                      <Input {...pField('website')} />
                    </div>
                    <div>
                      <Label>Telephone (optional)</Label>
                      <Input {...pField('phone')} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-8">
                <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-slate-500 mb-6">
                  Section 2 — Your work
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label>What are you currently working on that you are most passionate about?</Label>
                    <Area {...pField('passionate_about')} rows={4} />
                  </div>
                  <div>
                    <Label>What are you hoping to achieve over the next 12–24 months?</Label>
                    <Area {...pField('next_12_24_months')} rows={4} />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-8">
                <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-slate-500 mb-6">
                  Section 3 — Your expertise
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label>
                      What knowledge, skills, experience or expertise would you be happy to
                      contribute to SAFE?
                    </Label>
                    <Area {...pField('expertise_offer')} rows={4} />
                  </div>
                  <div>
                    <Label>Which areas would you most enjoy contributing to?</Label>
                    <p className="text-xs text-slate-500 mb-3">Select all that apply.</p>
                    <CheckGrid
                      testid="profile-contribution-areas"
                      options={ref.domains}
                      selected={profile.contribution_areas}
                      onToggle={toggle(setProfile, 'contribution_areas')}
                    />
                    <div className="mt-4">
                      <Label>Other</Label>
                      <Input {...pField('contribution_other')} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-8">
                <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-slate-500 mb-6">
                  Section 4 — Relationships &amp; networks
                </h3>
                <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                  SAFE's greatest strength is the collective experience and relationships of its
                  Council members.
                </p>
                <div className="space-y-6">
                  <div>
                    <Label>Which sectors or communities do you have active professional relationships with?</Label>
                    <p className="text-xs text-slate-500 mb-3">Select all that apply.</p>
                    <CheckGrid
                      testid="profile-sectors"
                      options={ref.sectors}
                      selected={profile.sectors}
                      onToggle={toggle(setProfile, 'sectors')}
                    />
                    <div className="mt-4">
                      <Label>Other</Label>
                      <Input {...pField('sectors_other')} />
                    </div>
                  </div>
                  <div>
                    <Label>Are there people, organisations or communities you believe SAFE should get to know?</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Please tell us who they are, how you know them, and why you believe they could
                      be valuable to SAFE's mission. Please share professional details only.
                    </p>
                    <Area {...pField('people_safe_should_know')} rows={5} />
                  </div>
                  <div>
                    <Label>
                      If the right opportunity arose, would you be happy to introduce SAFE to
                      appropriate people or organisations within your network?
                    </Label>
                    <div className="flex flex-wrap gap-6 mt-2">
                      {ref.intro_willingness.map((opt) => (
                        <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input
                            type="radio"
                            name="intro_willingness"
                            value={opt}
                            checked={profile.intro_willingness === opt}
                            onChange={() => setProfile((p) => ({ ...p, intro_willingness: opt }))}
                            className="w-4 h-4 accent-slate-900"
                            data-testid={`profile-intro-${opt.split(' ')[0].toLowerCase()}`}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-8">
                <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-slate-500 mb-6">
                  Section 5 — Opportunities
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label>Are there opportunities you believe SAFE should explore or be aware of?</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Research collaborations, university partnerships, grants or funding, strategic
                      partnerships, conferences or speaking, government initiatives, media, pilot
                      programmes, or anything else.
                    </p>
                    <Area {...pField('opportunities_to_explore')} rows={5} />
                  </div>
                  <div>
                    <Label>
                      Are there projects or initiatives where working together with SAFE could create
                      greater impact?
                    </Label>
                    <Area {...pField('joint_initiatives')} rows={4} />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-8">
                <h3 className="text-sm font-bold tracking-[0.12em] uppercase text-slate-500 mb-6">
                  Section 6 — Building SAFE together
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label>Is there anything SAFE could do to help support your work, organisation or mission?</Label>
                    <Area {...pField('how_safe_can_help')} rows={4} />
                  </div>
                  <div>
                    <Label>Is there another Council member you would like to know better or collaborate with?</Label>
                    <p className="text-xs text-slate-500 mb-2">Please tell us why.</p>
                    <Area {...pField('council_member_to_meet')} rows={3} />
                  </div>
                  <div>
                    <Label>As SAFE grows, who do you believe we should get to know?</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Individuals, organisations or communities who could strengthen SAFE through
                      research, universities, government, the United Nations, child protection, AI
                      and technology, law and policy, media, standards and assurance, philanthropy,
                      industry, future expert working groups or strategic partnerships. Please tell
                      us who they are, how you know them, and why they would strengthen SAFE.
                    </p>
                    <Area {...pField('who_should_we_know')} rows={5} />
                  </div>
                  <div>
                    <Label>Looking ahead, where do you believe you could make the greatest contribution to SAFE?</Label>
                    <Area {...pField('greatest_contribution')} rows={4} />
                  </div>
                  <div>
                    <Label>Is there anything else you would like the Council to know?</Label>
                    <Area {...pField('anything_else')} rows={4} />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-slate-200 p-8">
                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.consent}
                    onChange={(e) => setProfile((p) => ({ ...p, consent: e.target.checked }))}
                    className="mt-1 w-4 h-4 accent-slate-900 shrink-0"
                    data-testid="profile-consent"
                  />
                  <span className="leading-relaxed">
                    I am happy for SAFE to hold this information privately and to use it to
                    strengthen the Council, identify collaborations and make appropriate
                    introductions. <span className="text-rose-600">*</span>
                  </span>
                </label>
                <div className="flex flex-wrap items-center gap-4 mt-8">
                  <button type="submit" disabled={busy === 'profile'} className="btn-primary" data-testid="profile-submit">
                    {busy === 'profile' ? 'Submitting…' : 'Submit Partnership Profile'}
                  </button>
                  {draftSavedAt && (
                    <span className="flex items-center gap-2 text-xs text-slate-500">
                      <Save className="w-3.5 h-3.5" strokeWidth={1.75} />
                      Draft restored from this browser
                    </span>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ---------------- Referral ---------------- */}
      <section id="referral" className="scroll-mt-24 py-16 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="Step 2 of 5 — submit as many times as you like" title="Add a Referral">
            Someone you believe SAFE should get to know. Submit as many as you like, whenever you
            like — one person per submission.
          </SectionHeading>

          <div className="border-l-2 border-slate-900 bg-slate-50 p-6 mb-8">
            <p className="text-sm text-slate-700 leading-relaxed">
              Please provide only professional information that is reasonably relevant to a potential
              relationship with SAFE. Do not provide sensitive personal information about another
              person. Where possible, use publicly available professional details such as name,
              professional title, organisation, LinkedIn profile and professional website.
            </p>
          </div>

          {doneReferral ? (
            <Done
              testid="referral-success"
              title="Referral received."
              body="Thank you. SAFE will review this referral internally and decide on the right approach before any contact is made. Nothing happens automatically."
              onAgain={anotherReferral}
              againLabel="Add another referral"
            />
          ) : (
            <form onSubmit={submitReferral} className="space-y-8" data-testid="referral-form">
              <input type="text" name="company_fax" tabIndex={-1} autoComplete="off"
                value={referral.company_fax}
                onChange={(e) => setReferral((p) => ({ ...p, company_fax: e.target.value }))}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true" />

              <div className="bg-white border border-slate-200 p-8 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label required>Your name</Label>
                    <Input {...rField('referrer_name')} required />
                  </div>
                  <div>
                    <Label required>Your registered email address</Label>
                    <Input {...rField('referrer_email')} type="email" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label required>Their name</Label>
                    <Input {...rField('person_name')} required />
                  </div>
                  <div>
                    <Label>Their professional title</Label>
                    <Input {...rField('person_title')} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label>Their organisation</Label>
                    <Input {...rField('organisation')} />
                  </div>
                  <div>
                    <Label>Organisation website</Label>
                    <Input {...rField('organisation_website')} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label>Their LinkedIn profile</Label>
                    <Input {...rField('linkedin')} />
                  </div>
                  <div>
                    <Label>Their professional email (only if appropriate to share)</Label>
                    <Input {...rField('person_email')} type="email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label>Country</Label>
                    <Input {...rField('country')} />
                  </div>
                  <div>
                    <Label>Most relevant SAFE domain</Label>
                    <select {...rField('domain')} className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900">
                      <option value="">Select a domain</option>
                      {ref.domains.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Which sectors or communities are they part of?</Label>
                  <CheckGrid
                    testid="referral-sectors"
                    options={ref.sectors}
                    selected={referral.sectors}
                    onToggle={toggle(setReferral, 'sectors')}
                  />
                </div>
                <div>
                  <Label>How do you know them?</Label>
                  <Area {...rField('how_you_know_them')} rows={3} />
                </div>
                <div>
                  <Label>Why do you believe they could be valuable to SAFE's mission?</Label>
                  <Area {...rField('why_valuable')} rows={4} />
                </div>
                <div>
                  <Label>Would you be happy to make the introduction?</Label>
                  <div className="flex flex-wrap gap-6 mt-2">
                    {ref.intro_willingness.map((opt) => (
                      <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="radio"
                          name="introduction_available"
                          value={opt}
                          checked={referral.introduction_available === opt}
                          onChange={() => setReferral((p) => ({ ...p, introduction_available: opt }))}
                          className="w-4 h-4 accent-slate-900"
                          data-testid={`referral-intro-${opt.split(' ')[0].toLowerCase()}`}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <button type="submit" disabled={busy === 'referral'} className="btn-primary" data-testid="referral-submit">
                  {busy === 'referral' ? 'Submitting…' : 'Submit Referral'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ---------------- Opportunity ---------------- */}
      <section id="opportunity" className="scroll-mt-24 py-16 md:py-20 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="Step 3 of 5 — submit as many times as you like" title="Add an Opportunity">
            Research collaborations, grants, partnerships, conferences, government initiatives, media
            or pilot programmes. Submit as many as you like — one opportunity per submission.
          </SectionHeading>

          {doneOpportunity ? (
            <Done
              testid="opportunity-success"
              title="Opportunity received."
              body="Thank you. SAFE will assess this internally, set a priority and come back to you if we need anything further."
              onAgain={anotherOpportunity}
              againLabel="Add another opportunity"
            />
          ) : (
            <form onSubmit={submitOpportunity} className="space-y-8" data-testid="opportunity-form">
              <input type="text" name="company_fax" tabIndex={-1} autoComplete="off"
                value={opportunity.company_fax}
                onChange={(e) => setOpportunity((p) => ({ ...p, company_fax: e.target.value }))}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true" />

              <div className="bg-white border border-slate-200 p-8 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label required>Your name</Label>
                    <Input {...oField('submitter_name')} required />
                  </div>
                  <div>
                    <Label required>Your registered email address</Label>
                    <Input {...oField('submitter_email')} type="email" required />
                  </div>
                </div>
                <div>
                  <Label required>Opportunity title</Label>
                  <Input {...oField('title')} required placeholder="e.g. Joint research programme with…" />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label>Type of opportunity</Label>
                    <select {...oField('opportunity_type')} className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900">
                      <option value="">Select a type</option>
                      {ref.opportunity_types.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Most relevant SAFE domain</Label>
                    <select {...oField('domain')} className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900">
                      <option value="">Select a domain</option>
                      {ref.domains.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-6">
                  <div>
                    <Label>Organisation involved</Label>
                    <Input {...oField('organisation')} />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input {...oField('country')} />
                  </div>
                  <div>
                    <Label>Deadline (if any)</Label>
                    <Input {...oField('deadline')} type="date" />
                  </div>
                </div>
                <div>
                  <Label>Describe the opportunity</Label>
                  <Area {...oField('description')} rows={5} />
                </div>
                <div>
                  <Label>Why does it matter for SAFE?</Label>
                  <Area {...oField('why_it_matters')} rows={4} />
                </div>
                <div>
                  <Label>What support would you need from SAFE to pursue it?</Label>
                  <Area {...oField('support_needed')} rows={3} />
                </div>
                <button type="submit" disabled={busy === 'opportunity'} className="btn-primary" data-testid="opportunity-submit">
                  {busy === 'opportunity' ? 'Submitting…' : 'Submit Opportunity'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ---------------- Funding connection ---------------- */}
      <section id="funding" className="scroll-mt-24 py-16 md:py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="Step 4 of 5 — submit as many times as you like" title="Add a Funding Connection">
            SAFE needs capital from two very different worlds — commercial investment and
            philanthropic funding — and they behave nothing alike, so we record them separately.
            Tell us about one funder you can reach: the organisation, who you know there, the nature
            of the relationship and how far your access goes. Submit one per organisation and add
            more whenever a new relationship opens up. Nothing here is ever published, and we will
            never approach anyone without speaking to you first.
          </SectionHeading>

          {doneFunding ? (
            <Done
              testid="funding-success"
              title="Funding connection recorded."
              body="Thank you. We will bring this together with every other connection to the same organisation before anyone is approached, and come back to you before any introduction is made."
              onAgain={anotherFunding}
              againLabel="Add another funding connection"
            />
          ) : (
            <form onSubmit={submitFunding} className="space-y-8" data-testid="funding-form">
              <input type="text" name="company_fax" tabIndex={-1} autoComplete="off"
                value={funding.company_fax}
                onChange={(e) => setFunding((p) => ({ ...p, company_fax: e.target.value }))}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true" />

              <div className="bg-slate-50 border border-slate-200 p-8 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label required>Your name</Label>
                    <Input {...fField('submitter_name')} required />
                  </div>
                  <div>
                    <Label required>Your registered email address</Label>
                    <Input {...fField('submitter_email')} type="email" required />
                  </div>
                </div>

                <div>
                  <Label required>Which kind of funding is this?</Label>
                  <div className="flex flex-wrap gap-6 mt-2">
                    {(ref.funding_worlds || []).map((w) => (
                      <label key={w} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="radio"
                          name="funding_world"
                          value={w}
                          checked={funding.funding_world === w}
                          onChange={() => setFunding((p) => ({ ...p, funding_world: w, funder_type: '' }))}
                          className="w-4 h-4 accent-slate-900"
                          data-testid={`funding-world-${w.split(' ')[0].toLowerCase()}`}
                        />
                        {w}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label required>Organisation name</Label>
                    <Input {...fField('organisation')} required
                      placeholder="e.g. Ford Foundation, Sequoia Capital" />
                  </div>
                  <div>
                    <Label>Type of funder</Label>
                    <select {...fField('funder_type')}
                      className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900">
                      <option value="">{funding.funding_world ? 'Select a type' : 'Choose a funding kind first'}</option>
                      {((ref.funder_types || {})[funding.funding_world] || []).map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label>Who do you know there?</Label>
                    <Input {...fField('contact_name')} placeholder="Name of your contact" />
                  </div>
                  <div>
                    <Label>Their role at the organisation</Label>
                    <Input {...fField('contact_role')} placeholder="e.g. Programme Director, Partner" />
                  </div>
                </div>

                <div>
                  <Label>Nature of the relationship</Label>
                  <Area {...fField('relationship_nature')} rows={3}
                    placeholder="How you know them, how long you have known them, and how recently you have spoken." />
                </div>

                <div>
                  <Label required>How far does your access go?</Label>
                  <div className="space-y-2 mt-2" data-testid="funding-depth-group">
                    {(ref.access_depth || []).map((d) => (
                      <label key={d.label} className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="radio"
                          name="access_depth"
                          value={d.label}
                          required
                          checked={funding.access_depth === d.label}
                          onChange={() => setFunding((p) => ({ ...p, access_depth: d.label }))}
                          className="mt-1 w-4 h-4 accent-slate-900 shrink-0"
                          data-testid={`funding-depth-${d.score}`}
                        />
                        <span>{d.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label>How many people do you know there?</Label>
                    <select {...fField('contacts_at_org')}
                      className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900">
                      <option value="">Select</option>
                      {(ref.contacts_at_org || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Typical amount they fund or invest</Label>
                    <select {...fField('typical_ticket')}
                      className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900">
                      <option value="">Select a band</option>
                      {(ref.ticket_bands || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Which regions can this connection reach?</Label>
                  <CheckGrid
                    options={ref.funding_regions || []}
                    selected={funding.regions}
                    onToggle={toggle(setFunding, 'regions')}
                    testid="funding-regions"
                  />
                </div>

                <div>
                  <Label>What would you be comfortable doing?</Label>
                  <div className="space-y-2 mt-2">
                    {(ref.funding_willingness || []).map((w, wi) => (
                      <label key={w} className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="radio"
                          name="funding_willingness"
                          value={w}
                          checked={funding.willingness === w}
                          onChange={() => setFunding((p) => ({ ...p, willingness: w }))}
                          className="mt-1 w-4 h-4 accent-slate-900 shrink-0"
                          data-testid={`funding-willingness-${wi}`}
                        />
                        <span>{w}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Anything else SAFE should know before approaching them</Label>
                  <Area {...fField('notes')} rows={3}
                    placeholder="Timing, sensitivities, what they fund, who else is involved." />
                </div>

                <button type="submit" disabled={busy === 'funding'} className="btn-primary" data-testid="funding-submit">
                  {busy === 'funding' ? 'Submitting…' : 'Submit Funding Connection'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ---------------- Police check & identity verification ---------------- */}
      <section id="vetting" className="scroll-mt-24 py-16 md:py-20 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeading eyebrow="Step 5 of 5 — required once" title="Police Check & Identity Verification">
            SAFE Council members work in child safeguarding, so we verify the identity and criminal
            history of everyone in the network. Please upload two documents. They are held in
            secure private storage, are visible only to authorised SAFE administrators, are never
            published or shared, and are used solely to verify your identity and clearance.
          </SectionHeading>

          {doneVetting ? (
            <Done
              testid="vetting-success"
              title="Documents received securely."
              body="Thank you. Your criminal history check and identity photo have been stored securely and are visible only to authorised SAFE administrators. We will confirm once verification is complete, and will contact you if anything further is needed."
            />
          ) : (
            <form onSubmit={submitVetting} className="space-y-8" data-testid="vetting-form">
              <input type="text" name="company_fax" tabIndex={-1} autoComplete="off"
                value={vetting.company_fax}
                onChange={(e) => setVetting((p) => ({ ...p, company_fax: e.target.value }))}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true" />

              <div className="bg-white border border-slate-200 p-8 space-y-8">
                <div className="border-l-2 border-slate-900 bg-slate-50 p-5 space-y-3">
                  <p className="text-sm font-bold text-slate-900">What we need</p>
                  <ol className="text-sm text-slate-700 leading-relaxed space-y-2 list-decimal pl-5">
                    <li>
                      <strong>A national criminal history check</strong> issued within the last
                      6 months, as a PDF. In Australia this is a Nationally Coordinated Criminal
                      History Check (NCCHC); elsewhere please provide your country's national or
                      federal equivalent (for example an ACRO Police Certificate in the UK, an FBI
                      Identity History Summary in the US, or an RCMP criminal record check in
                      Canada).
                    </li>
                    <li>
                      <strong>A photo of yourself holding your photo ID</strong> — passport,
                      national identity card or driver's licence — with your face and the details
                      on the ID both clearly readable.
                    </li>
                  </ol>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Maximum 10 MB per file. If a document is larger, please compress it before
                    uploading. If your check is in a language other than English, please include a
                    translation.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label required>Your full legal name</Label>
                    <Input {...vField('submitter_name')} required />
                  </div>
                  <div>
                    <Label required>Your registered email address</Label>
                    <Input {...vField('submitter_email')} type="email" required />
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-6">
                  <div>
                    <Label>Country that issued the check</Label>
                    <Input {...vField('check_country')} placeholder="e.g. Australia" />
                  </div>
                  <div>
                    <Label>Date the check was issued</Label>
                    <Input {...vField('check_issued_on')} type="date" />
                  </div>
                  <div>
                    <Label>Type of photo ID</Label>
                    <select {...vField('id_document_type')}
                      className="w-full px-4 py-3 border border-slate-300 text-sm bg-white focus:outline-none focus:border-slate-900">
                      <option value="">Select</option>
                      {ID_DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label required>Criminal history check (PDF)</Label>
                    <label className="flex items-center gap-3 border border-dashed border-slate-300 px-4 py-4 cursor-pointer hover:border-slate-900 transition-colors">
                      <FileUp className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.75} />
                      <span className="text-sm text-slate-700 truncate">
                        {policeCheckFile ? policeCheckFile.name : 'Choose a PDF file'}
                      </span>
                      <input type="file" accept="application/pdf" className="hidden"
                        onChange={(e) => setPoliceCheckFile(e.target.files?.[0] || null)}
                        data-testid="vetting-police-check-input" />
                    </label>
                  </div>
                  <div>
                    <Label required>Photo of you holding your ID</Label>
                    <label className="flex items-center gap-3 border border-dashed border-slate-300 px-4 py-4 cursor-pointer hover:border-slate-900 transition-colors">
                      <FileUp className="w-5 h-5 text-slate-500 shrink-0" strokeWidth={1.75} />
                      <span className="text-sm text-slate-700 truncate">
                        {idPhotoFile ? idPhotoFile.name : 'Choose a JPEG, PNG or WebP image'}
                      </span>
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/heic"
                        className="hidden"
                        onChange={(e) => setIdPhotoFile(e.target.files?.[0] || null)}
                        data-testid="vetting-id-photo-input" />
                    </label>
                  </div>
                </div>

                <div>
                  <Label>Anything we should know</Label>
                  <Area {...vField('notes')} rows={3}
                    placeholder="Optional — for example if your check is still being processed, or was issued under a previous name." />
                </div>

                <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={vetting.consent}
                    onChange={(e) => setVetting((p) => ({ ...p, consent: e.target.checked }))}
                    className="mt-1 w-4 h-4 accent-slate-900 shrink-0"
                    data-testid="vetting-consent" />
                  <span>
                    I confirm these documents are genuine and relate to me, and I consent to SAFE
                    holding and verifying them for the purpose of confirming my identity and
                    criminal history clearance.
                  </span>
                </label>

                <button type="submit" disabled={busy === 'vetting'} className="btn-primary"
                  data-testid="vetting-submit">
                  {busy === 'vetting' ? 'Uploading securely…' : 'Submit your police check'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm text-slate-400 leading-relaxed">
            Thank you for taking the time. Our hope is that this is the beginning of many
            conversations, collaborations and opportunities where we can support one another while
            building SAFE together. Questions? Write to{' '}
            <a href="mailto:safe@safestandard.ai" className="text-white underline underline-offset-4 hover:no-underline">
              safe@safestandard.ai
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PartnershipHubPage;
